import { useState, useEffect, useCallback } from 'react';

import toast from 'components/partials/toast/toast';

import {
  fetchMonitoringSettings,
  saveMonitoringSettings,
} from 'api/monitoring';
import { getClientCohorts } from 'api/generated/client-cohorts/client-cohorts';

import { unmountModal } from 'helpers/modal-render';
import {
  generateYear1Options,
  getCurrentFiscalYear,
} from 'containers/admin/clients/client/tabs-components/monitoring-settings-modal/monitoring-settings.helpers';
import type {
  CohortOption,
  MonitoringFormValues,
} from 'containers/admin/clients/client/tabs-components/monitoring-settings-modal/monitoring-settings.constants';

export interface UseMonitoringSettingsParams {
  clientId: string | number | undefined;
  userId: string | number | undefined;
  reset: (values: MonitoringFormValues) => void;
}

export interface UseMonitoringSettingsResult {
  loading: boolean;
  submitting: boolean;
  cohortOptions: CohortOption[];
  onSubmit: (data: MonitoringFormValues) => Promise<void>;
}

export const useMonitoringSettings = ({
  clientId,
  userId,
  reset,
}: UseMonitoringSettingsParams): UseMonitoringSettingsResult => {
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [cohortOptions, setCohortOptions] = useState<CohortOption[]>([]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        if (!clientId || !userId) return;

        const userIdStr = String(userId);
        const numericClientId = Number(clientId);

        const [settings, fetchedCohorts] = await Promise.all([
          fetchMonitoringSettings(userIdStr, numericClientId),
          getClientCohorts(userIdStr, numericClientId),
        ]);

        const options: CohortOption[] = fetchedCohorts.map((cohort) => ({
          label: cohort.name ?? '',
          value: cohort.name ?? '',
        }));

        setCohortOptions(options);

        if (settings?.monitoring) {
          const m = settings.monitoring;

          reset({
            cohort: options.find((opt) => opt.value === m.cohort) || null,
            comparisonYear1:
              generateYear1Options().find(
                (y) => y.value === m.comparisonYears?.[0]
              ) || null,
            comparisonYear2: {
              label: String(getCurrentFiscalYear()),
              value: getCurrentFiscalYear(),
            },
            inquiriesToApps: m.inquiriesToApps || '',
            applicationsToAdmits: m.applicationsToAdmits || '',
            admitsToEnrollment: m.admitsToEnrollment || '',
          });
        }
      } catch (error) {
        console.error('Error fetching monitoring settings or cohorts', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [clientId, userId, reset]);

  const onSubmit = useCallback(
    async (data: MonitoringFormValues): Promise<void> => {
      setSubmitting(true);
      const payload = {
        monitoring: {
          cohort: data.cohort?.value || '',
          comparisonYears: [
            data.comparisonYear1?.value ?? getCurrentFiscalYear(),
            getCurrentFiscalYear(),
          ],
          inquiriesToApps: data.inquiriesToApps,
          applicationsToAdmits: data.applicationsToAdmits,
          admitsToEnrollment: data.admitsToEnrollment,
        },
      };

      try {
        if (!userId || !clientId) return;

        await saveMonitoringSettings(String(userId), clientId, payload);
        toast.success({ title: 'Monitoring settings saved successfully' });
        unmountModal();
      } catch (error) {
        console.error('Error updating monitoring settings', error);
        toast.error({ title: 'Failed to save monitoring settings' });
      } finally {
        setSubmitting(false);
      }
    },
    [clientId, userId]
  );

  return {
    loading,
    submitting,
    cohortOptions,
    onSubmit,
  };
};
